import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, type DevicePendingSummary } from '../api/client';
import { CAMBIO_DE_DISPOSITIVOS } from '../screens/Dispositivos';
import { POSPONER_MS, VUELTA_MS, decidirAviso, type Decision } from '../dispositivos/aviso';
import { IconoCampana, IconoDispositivos, IconoRechazar } from './Iconos';

/**
 * Lo que espera aprobación, para quien aprueba (ADR-0026), preguntado cada
 * pocos segundos. La consulta es de fondo: no alarga la sesión, así que una
 * pestaña olvidada no la mantiene viva. Se vuelve a preguntar al momento al
 * decidir en la bandeja y al volver a la pestaña.
 */
export function usePendientesDeDispositivos(activo: boolean): DevicePendingSummary | null {
  // `null` hasta la primera respuesta: un «cero» inventado gastaba la primera
  // vuelta y lo que ya esperaba al entrar salía como «nueva solicitud».
  const [resumen, setResumen] = useState<DevicePendingSummary | null>(null);

  useEffect(() => {
    if (!activo) return;
    let vigente = true;
    const preguntar = () =>
      api
        .devicePendingCount()
        .then((r) => vigente && setResumen(r))
        .catch(() => undefined);
    const alVolver = () => {
      if (!document.hidden) void preguntar();
    };
    void preguntar();
    const vuelta = window.setInterval(() => void preguntar(), VUELTA_MS);
    window.addEventListener(CAMBIO_DE_DISPOSITIVOS, preguntar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vigente = false;
      window.clearInterval(vuelta);
      window.removeEventListener(CAMBIO_DE_DISPOSITIVOS, preguntar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [activo]);

  return resumen;
}

/**
 * La campana de la barra superior. Con algo pendiente se mueve y late —el
 * movimiento es lo que la saca del rabillo del ojo— y lleva el número; sin nada,
 * se queda quieta y discreta. Lleva a la bandeja.
 */
export function CampanaDeDispositivos({ pendientes }: { pendientes: number }) {
  const { t } = useTranslation();
  const etiqueta = t('devices.notify.bell', { count: pendientes });
  return (
    <Link
      to="/dispositivos"
      className={`campana${pendientes > 0 ? ' campana--activa' : ''}`}
      aria-label={etiqueta}
      title={etiqueta}
    >
      <span className="campana__icono">
        <IconoCampana />
      </span>
      {pendientes > 0 && <span className="campana__cuenta">{pendientes}</span>}
    </Link>
  );
}

const clavePospuesto = (userId: string) => `cpsp:dispositivos:posponer:${userId}`;

function leerPospuesto(userId: string): number {
  try {
    return Number(window.localStorage.getItem(clavePospuesto(userId))) || 0;
  } catch {
    return 0;
  }
}

function guardarPospuesto(userId: string, hasta: number): void {
  try {
    window.localStorage.setItem(clavePospuesto(userId), String(hasta));
  } catch {
    // Sin almacenamiento sólo se pierde el «después»: el aviso vuelve antes.
  }
}

/**
 * El aviso que no deja que se olvide: entra deslizándose arriba a la derecha
 * cuando llega una solicitud nueva —y, si la pestaña está en segundo plano,
 * también como aviso del sistema—, y vuelve cada diez minutos mientras siga
 * habiendo pendientes. «Recordármelo después» lo calla diez minutos; una
 * solicitud nueva lo trae de vuelta aunque esté pospuesto.
 */
export function AvisoDeDispositivos({ resumen, userId }: { resumen: DevicePendingSummary | null; userId: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navegar = useNavigate();
  const enLaBandeja = pathname.startsWith('/dispositivos');
  const [tipo, setTipo] = useState<Exclude<Decision, 'ocultar' | 'igual'> | null>(null);
  const [permiso, setPermiso] = useState<NotificationPermission | 'no-hay'>(
    typeof Notification === 'undefined' ? 'no-hay' : Notification.permission,
  );
  const vista = useRef<string | null>(null);
  const primeraVez = useRef(true);

  const nombreDe = useCallback(
    (d: NonNullable<DevicePendingSummary['latest']>) =>
      `${d.fullName} — ${t('devices.name', { kind: t(`devices.kind.${d.kind}`), browser: d.browser, os: d.os })}`,
    [t],
  );

  useEffect(() => {
    if (!resumen) return;
    const decision = decidirAviso({
      pendientes: resumen.pending,
      ultimaId: resumen.latest?.id,
      vistaId: vista.current,
      primeraVez: primeraVez.current,
      mostrando: tipo !== null,
      enLaBandeja,
      ahora: Date.now(),
      posponerHasta: leerPospuesto(userId),
    });
    primeraVez.current = false;
    vista.current = resumen.latest?.id ?? vista.current;

    if (decision === 'ocultar') setTipo(null);
    if (decision === 'recordatorio') setTipo('recordatorio');
    if (decision === 'nuevo') {
      setTipo('nuevo');
      // En otra pestaña, el aviso del portal no se ve: el del sistema sí.
      if (document.hidden && permiso === 'granted' && resumen.latest) {
        const aviso = new Notification(t('devices.notify.systemTitle', { count: resumen.pending }), {
          body: nombreDe(resumen.latest),
          tag: 'cpsp-dispositivos',
        });
        aviso.onclick = () => {
          window.focus();
          navegar('/dispositivos');
          aviso.close();
        };
      }
    }
    // `tipo` y `permiso` no disparan una decisión: sólo lo hace lo que llega.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen, enLaBandeja, userId]);

  // Mientras siga pendiente y pospuesto, se vuelve a mirar cuando se cumple el
  // plazo aunque no llegue nada nuevo.
  useEffect(() => {
    if (tipo !== null || !resumen || resumen.pending === 0 || enLaBandeja) return;
    const falta = leerPospuesto(userId) - Date.now();
    if (falta <= 0) return;
    const reloj = window.setTimeout(() => setTipo('recordatorio'), falta);
    return () => window.clearTimeout(reloj);
  }, [tipo, resumen, enLaBandeja, userId]);

  if (!tipo || !resumen || resumen.pending === 0) return null;

  const posponer = () => {
    guardarPospuesto(userId, Date.now() + POSPONER_MS);
    setTipo(null);
  };

  return createPortal(
    <section
      className={`aviso-dispositivos aviso-dispositivos--${tipo}`}
      role="alert"
      aria-live="assertive"
    >
      <span className="aviso-dispositivos__sello" aria-hidden="true">
        <IconoDispositivos />
      </span>
      <div className="aviso-dispositivos__texto">
        <span className="aviso-dispositivos__antetitulo">
          {t(tipo === 'nuevo' ? 'devices.notify.new' : 'devices.notify.reminder')}
        </span>
        <strong className="aviso-dispositivos__titulo">
          {t('devices.notify.title', { count: resumen.pending })}
        </strong>
        {resumen.latest && (
          <span className="aviso-dispositivos__detalle">
            {t('devices.notify.latest', { who: nombreDe(resumen.latest) })}
          </span>
        )}
        <div className="aviso-dispositivos__acciones">
          <button
            className="boton"
            type="button"
            onClick={() => {
              setTipo(null);
              navegar('/dispositivos');
            }}
          >
            {t('devices.notify.review')}
          </button>
          <button className="boton boton--fantasma" type="button" onClick={posponer}>
            {t('devices.notify.later')}
          </button>
        </div>
        {/* Que también le avise el sistema cuando esté en otra pestaña. Sólo se
            ofrece mientras el navegador no tenga respuesta. */}
        {permiso === 'default' && (
          <button
            className="aviso-dispositivos__escritorio"
            type="button"
            onClick={() => void Notification.requestPermission().then(setPermiso)}
          >
            {t('devices.notify.desktop')}
          </button>
        )}
      </div>
      <button className="aviso-dispositivos__cerrar" type="button" aria-label={t('devices.notify.later')} onClick={posponer}>
        <IconoRechazar />
      </button>
    </section>,
    document.body,
  );
}
